import PropTypes from 'prop-types';
import { compose, isNil, none } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, PKUrl, SidebarInfo, SidebarInfoColumn } from 'components';
import SidebarSection from 'components/atoms/GefMui/SidebarSection';
import { codelists } from 'constants/codelists';
import { getLeadData } from 'features/views/leadDetail/selectors';
import { getCodelistPropById, toNormalDate } from 'utils';

export const SidebarPolicyInfo = ({ products, policyStatuses }) => {
	const leadDetail = useSelector(getLeadData);

	const { client = {}, policy = {}, claimNo, id, clientLoadedFromCcd } = leadDetail;
	const { id: partnerId } = client;
	const { policyNo, systemId, policyStatusId, productLines } = policy;

	const showSection = !isNilOrEmpty(policy);

	const productLinesData = {
		products,
		productLines,
	};

	const policyStatusData = {
		policyStatuses,
		policyStatusId,
	};

	const getProductLines = () => {
		if (!isNilOrEmpty(productLinesData.productLines)) {
			return productLinesData.productLines
				.map((pL) => getCodelistPropById(pL, productLinesData.products, 'shortDescription'))
				.join(', ');
		}
	};

	const getPolicyStatus = () =>
		policyStatusData.policyStatusId &&
		getCodelistPropById(policyStatusData.policyStatusId, policyStatusData.policyStatuses);

	const data = [
		{
			attribute: getLabelMessage('leadDetail:sidebarProposalNumber'),
			hasValue: !isNilOrEmpty(policy?.proposalNo),
			finalValue: policy.proposalNo,
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarInsurance'),
			hasValue: none(isNil, [productLinesData.productLines, productLinesData.products]),
			finalValue: getProductLines(productLinesData),
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarPSStatus'),
			hasValue: none(isNil, [policyStatusData.policyStatuses, policyStatusData.policyStatusId]),
			finalValue: getPolicyStatus(policyStatusData),
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarDateNegotiation'),
			hasValue: !isNilOrEmpty(policy?.policyOpenDate),
			finalValue: toNormalDate(policy.policyOpenDate),
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarDateBegining'),
			hasValue: !isNilOrEmpty(policy?.policyValueDate),
			finalValue: toNormalDate(policy.policyValueDate),
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarDateEnd'),
			hasValue: !isNilOrEmpty(policy?.policyFinalDate),
			finalValue: toNormalDate(policy.policyFinalDate),
		},
		{
			attribute: getLabelMessage('leadDetail:sidebarClaimNo'),
			hasValue: !isNilOrEmptyString(claimNo),
			finalValue: claimNo,
		},
	];

	return (
		showSection && (
			<SidebarSection heading={getLabelMessage('leadDetail:sidebarLeadDetailPolicyInfo')}>
				<SidebarInfo>
					{!isNilOrEmptyString(policyNo) && (
						<SidebarInfoColumn
							attribute={getLabelMessage('leadDetail:sidebarPSNumber')}
							value={
								<PKUrl.PKPolicy
									partnerId={partnerId}
									policyNo={policyNo}
									systemId={clientLoadedFromCcd && systemId}
									taskId={id}
									unlock
									muiLink
								>
									{policyNo || '---'}
								</PKUrl.PKPolicy>
							}
						/>
					)}

					{data.map(
						(item) =>
							item.hasValue && <SidebarInfoColumn attribute={item.attribute} value={item.finalValue} />,
					)}
				</SidebarInfo>
			</SidebarSection>
		)
	);
};

SidebarPolicyInfo.propTypes = {
	policyStatuses: PropTypes.array,
	products: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.products, 'products'),
	codelistProvider(codelists.policyStatuses, 'policyStatuses'),
)(SidebarPolicyInfo);
